import React, { useState, useEffect } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Link } from "react-router-dom";
import api from "../../api";
import {
  handleDownloadReceipt,
  handleShareReceipt,
} from "../../utils/receiptUtils";
import { useTheme } from "../../components/header/header";
import "./transaction-history.css";

import back from "./img/back.svg";
import backDark from "./img/back-dark.svg";
import download from "./img/download.svg";
import downloadDark from "./img/download-dark.svg";
import share from "./img/share.svg";
import shareDark from "./img/share-dark.svg";
import close from "./img/close.svg";
import closeDark from "./img/close-dark.svg";
import copyDone from "./img/copy-done.svg";
import copyDoneDark from "./img/copy-done-dark.svg";
import copyIcon from "./img/copy-icon.svg";
import copyIconDark from "./img/copy-icon-dark.svg";

const TransactionHistory = () => {
  const { theme } = useTheme();
  const [transactions, setTransactions] = useState([]);
  const [modalType, setModalType] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedInvoiceId, setCopiedInvoiceId] = useState(null);
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    const hasReloaded = localStorage.getItem("hasReloaded");

    if (!hasReloaded) {
      window.location.reload();
      localStorage.setItem("hasReloaded", "true");
    }

    return () => {
      localStorage.removeItem("hasReloaded");
    };
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await api.get(`/api/transactions/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setTransactions(response.data);
      } catch (err) {
        if (err.response) {
          setError(err.response.data.error.message);
        } else {
          setError("An error occurred while fetching transactions.");
        }
      }
    };

    fetchTransactions();
  }, [userId]);

  const openModal = (type, transaction) => {
    setModalType(type);
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedTransaction(null);
    setIsModalOpen(false);
    document.body.style.overflow = "";
  };

  const handleCopy = (invoiceId) => {
    setCopiedInvoiceId(invoiceId);
    setTimeout(() => {
      setCopiedInvoiceId(null);
    }, 3000);
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const capitalizeFirstLetter = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const handleDownloadReceiptWrapper = () => {
    if (!selectedTransaction) return;

    handleDownloadReceipt(
      () => Promise.resolve(selectedTransaction),
      selectedTransaction.transaction_id
    ).catch((err) => setError(err.message));
  };

  const handleShareReceiptWrapper = () => {
    if (!selectedTransaction) return;

    handleShareReceipt(selectedTransaction).catch((err) =>
      setError(err.message)
    );
  };

  return (
    <>
      <div className="headerPayment">
        <Link className="backBtn" to={"/deposit"}>
          <img src={theme === "light" ? back : backDark} alt="back" />
        </Link>
        <div className="title">Transaction History</div>
      </div>
      <div className="container">
        {error && <div className="error">{error}</div>}
        {!error && (
          <div className="historyTableWrap">
            <table className="historyTable">
              <thead>
                <tr>
                  <th scope="col">Invoice ID</th>
                  <th scope="col">Date/Time</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Payment Method</th>
                  <th scope="col">Payment Status</th>
                  <th scope="col">Note</th>
                  <th scope="col">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <th scope="row">
                      <span>{transaction.invoice_id?.slice(0, 5)}</span>
                      <CopyToClipboard
                        text={transaction.invoice_id}
                        onCopy={() => handleCopy(transaction.invoice_id)}
                      >
                        <img
                          className={`addressCopy addressCopyHistory ${
                            copiedInvoiceId !== transaction.invoice_id &&
                            "addressCopyWrap"
                          }`}
                          src={
                            copiedInvoiceId === transaction.invoice_id
                              ? theme === "light"
                                ? copyDone
                                : copyDoneDark
                              : theme === "light"
                              ? copyIcon
                              : copyIconDark
                          }
                          alt="copy"
                        />
                      </CopyToClipboard>
                    </th>
                    <td className="tdDate">
                      {(() => {
                        const date = new Date(transaction.created_at);
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = String(date.getMonth() + 1).padStart(
                          2,
                          "0"
                        );
                        const year = String(date.getFullYear()).slice(-2);

                        const hours = date.getHours();
                        const minutes = String(date.getMinutes()).padStart(
                          2,
                          "0"
                        );
                        const ampm = hours >= 12 ? "PM" : "AM";
                        const formattedHours = hours % 12 || 12;

                        return `${day}/${month}/${year} - ${formattedHours}:${minutes} ${ampm}`;
                      })()}
                    </td>
                    <td>${transaction.amount}</td>
                    <td className="tdMethod">{transaction.payment_method}</td>
                    <td
                      className={
                        transaction.status === "COMPLETED"
                          ? "green"
                          : transaction.status === "FAILED"
                          ? "red"
                          : ""
                      }
                    >
                      {capitalizeFirstLetter(transaction.status)}
                    </td>
                    <td onClick={() => openModal("receipt", transaction)}>
                      View
                    </td>
                    <td onClick={() => openModal("note", transaction)}>View</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && modalType && selectedTransaction && (
        <div className="modalNoteWrap">
          <div className="modalNote">
            <img
              className="modalClose"
              onClick={() => closeModal()}
              src={theme === "light" ? close : closeDark}
              alt="close"
            />
            {modalType === "receipt" ? (
              <>
                <div className="title titleModal">Receipt</div>
                <div className="borderWrap">
                  <div className="table">
                    <div className="tableRow">
                      <div>Invoice ID:</div>
                      <div>{selectedTransaction.invoice_id?.slice(0, 5)}</div>
                    </div>
                    <div className="tableRow">
                      <div>Cashier ID:</div>
                      <div>{selectedTransaction.cashier_id}</div>
                    </div>
                    <div className="tableRow">
                      <div>Date/Time:</div>
                      <div>{selectedTransaction.created_at}</div>
                    </div>
                    <div className="tableRow">
                      <div>Payment Method:</div>
                      <div>{selectedTransaction.payment_method}</div>
                    </div>
                    <div className="tableRow">
                      <div>Amount:</div>
                      <div>${selectedTransaction.amount}</div>
                    </div>
                  </div>
                  <div className="noteBg">
                    Please send this receipt to your cashier, or let them know
                    you have completed the payment.
                  </div>
                  <div className="complitedText">
                    DO NOT RESEND PAYMENT TO THIS BITCOIN ADDRESS AGAIN, WE ARE
                    NOT RESPONSIBLE FOR ANY LOSSES THAT GET SENT TO THE WRONG
                    BITCOIN ADDRESS
                  </div>
                  <div className="btnsWrap">
                    <button
                      className="btn"
                      onClick={handleDownloadReceiptWrapper}
                    >
                      <div>Download</div>
                      <img
                        src={theme === "light" ? download : downloadDark}
                        alt="download"
                      />
                    </button>
                    <button className="btn" onClick={handleShareReceiptWrapper}>
                      <div>Share</div>
                      <img
                        src={theme === "light" ? share : shareDark}
                        alt="share"
                      />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="title titleModal">Note</div>
                <div className="modalNoteBg">{selectedTransaction.note}</div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionHistory;
